import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall, getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import './PersonalizarContrato.css';

const COR = 'rgb(235, 133, 0)';
const campo = (o, nome) => o?.[nome] ?? o?.[nome.charAt(0).toUpperCase() + nome.slice(1)];

// Mesmo texto do PDF: 1 → PRIMEIRA, 13 → DECIMA TERCEIRA, 20 → VIGESIMA
const UNIDADES = ['', 'PRIMEIRA', 'SEGUNDA', 'TERCEIRA', 'QUARTA', 'QUINTA', 'SEXTA', 'SETIMA', 'OITAVA', 'NONA'];
const DEZENAS = ['', 'DECIMA', 'VIGESIMA', 'TRIGESIMA', 'QUADRAGESIMA', 'QUINQUAGESIMA', 'SEXAGESIMA', 'SEPTUAGESIMA', 'OCTOGESIMA', 'NONAGESIMA'];
const ordinal = (n) => (n <= 0 || n >= 100 ? String(n) : [DEZENAS[Math.floor(n / 10)], UNIDADES[n % 10]].filter(Boolean).join(' '));

let proximoId = 1;
const novoId = () => proximoId++;

// Do servidor ({ titulo, subclausulas: [texto] }) para o editor (com ids para arrastar)
const paraEditor = (lista) => (Array.isArray(lista) ? lista : []).map(c => ({
  id: novoId(),
  titulo: campo(c, 'titulo') || '',
  tipo: campo(c, 'tipo') || null,
  subs: (campo(c, 'subclausulas') || []).map(texto => ({ id: novoId(), texto }))
}));
const paraServidor = (clausulas) => clausulas.map(c => ({ titulo: c.titulo, tipo: c.tipo, subclausulas: c.subs.map(s => s.texto) }));
// Cláusula dos serviços (lista dos serviços e valores): obrigatória e não editável; só muda de posição
const ehServicos = (c) => c?.tipo === 'servicos';

// Textarea que cresce com o texto (fonte fixa, sem opções de formatação)
function TextoAuto({ value, onChange, placeholder, onFocus }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) { ref.current.style.height = 'auto'; ref.current.style.height = `${ref.current.scrollHeight}px`; }
  }, [value]);
  return <textarea ref={ref} rows={1} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} onFocus={onFocus} onSelect={onFocus} onKeyUp={onFocus} onClick={onFocus} />;
}

function Alca({ onDragStart, onDragEnd }) {
  return (
    <div className="pc-bloco-alca" title="Arrastar" draggable onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className="pc-ponto" />)}
    </div>
  );
}

// Personalizar o contrato (PDF): Padrão = cláusulas do sistema; Personalizado = cláusulas editadas aqui.
// Arraste os containers para mudar a ordem; a numeração (CLÁUSULA DECIMA TERCEIRA, 13.1...) é pela posição.
export default function PersonalizarContrato() {
  const navigate = useNavigate();
  const [modo, setModo] = useState('padrao');
  const [clausulas, setClausulas] = useState([]);
  const [padrao, setPadrao] = useState([]);
  const [marcadores, setMarcadores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [alterado, setAlterado] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmacao, setConfirmacao] = useState(null); // 'personalizado' | 'padrao' | 'restaurar' | null
  // Aviso "cláusula obrigatória" ao tentar remover a cláusula dos serviços (id da cláusula)
  const [avisoObrigatoria, setAvisoObrigatoria] = useState(null);
  // Último campo de texto focado: { ci, si } (subcláusula) ou { ci, titulo: true }, com a posição do cursor
  const focoRef = useRef(null);
  // Visualizar: PDF de exemplo com as cláusulas da tela
  const [previa, setPrevia] = useState(null); // { url } | null
  const [gerandoPrevia, setGerandoPrevia] = useState(false);

  // Arrastar: { tipo: 'clausula', ci } ou { tipo: 'sub', ci, si }; alvo: { tipo, ci, si, lado }
  const arrastoRef = useRef(null);
  const [arrastando, setArrastando] = useState(null);
  const [alvo, setAlvo] = useState(null);

  const aplicar = (r) => {
    setModo(campo(r, 'modo') === 'personalizado' ? 'personalizado' : 'padrao');
    setClausulas(paraEditor(campo(r, 'clausulas')));
    setPadrao(paraEditor(campo(r, 'padrao')));
    setMarcadores(campo(r, 'marcadores') || []);
    setAlterado(false);
  };

  useEffect(() => {
    document.title = 'Personalizar contrato';
    (async () => {
      try {
        aplicar(await apiCall('ContratoModelo'));
      } catch (e) {
        setErro('Não foi possível carregar o contrato.');
      } finally {
        setCarregando(false);
      }
    })();
  }, []);

  const mensagemDe = (e) => {
    let msg = e.message || String(e);
    try { msg = JSON.parse(msg).mensagem || msg; } catch { /* texto simples */ }
    return msg;
  };

  const salvar = async (novoModo = modo) => {
    try {
      setSalvando(true);
      setErro('');
      aplicar(await apiCall('ContratoModelo', 'PUT', { modo: novoModo, clausulas: paraServidor(clausulas) }));
      return true;
    } catch (e) {
      setErro(mensagemDe(e));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const confirmar = async () => {
    const tipo = confirmacao;
    setConfirmacao(null);
    if (tipo === 'personalizado') {
      setModo('personalizado');
      setAlterado(true);
    } else if (tipo === 'padrao') {
      await salvar('padrao');
    } else if (tipo === 'restaurar') {
      try {
        setSalvando(true);
        aplicar(await apiCall('ContratoModelo/restaurar-padrao', 'POST'));
      } catch (e) {
        setErro(mensagemDe(e));
      } finally {
        setSalvando(false);
      }
    }
  };

  const fechar = () => {
    if (alterado && !window.confirm('Sair sem salvar? As alterações serão perdidas.')) return;
    if (window.opener) window.close();
    else navigate('/sidebar/ContratoDropdown');
  };

  const alterar = (fn) => { setClausulas(prev => fn(prev)); setAlterado(true); };
  const alterarTitulo = (ci, valor) => alterar(prev => prev.map((c, k) => (k === ci ? { ...c, titulo: valor } : c)));
  const alterarSub = (ci, si, valor) => alterar(prev => prev.map((c, k) => (k === ci ? { ...c, subs: c.subs.map((s, m) => (m === si ? { ...s, texto: valor } : s)) } : c)));
  const adicionarSub = (ci) => alterar(prev => prev.map((c, k) => (k === ci ? { ...c, subs: [...c.subs, { id: novoId(), texto: '' }] } : c)));
  const removerSub = (ci, si) => alterar(prev => prev.map((c, k) => (k === ci ? { ...c, subs: c.subs.filter((_, m) => m !== si) } : c)));
  const adicionarClausula = () => {
    alterar(prev => [...prev, { id: novoId(), titulo: '', subs: [{ id: novoId(), texto: '' }] }]);
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 50);
  };
  const removerClausula = (ci) => {
    if (ehServicos(clausulas[ci])) {
      setAvisoObrigatoria(clausulas[ci].id);
      setTimeout(() => setAvisoObrigatoria(atual => (atual === clausulas[ci].id ? null : atual)), 3500);
      return;
    }
    if (!window.confirm(`Remover a CLÁUSULA ${ordinal(ci + 1)} e todas as subcláusulas dela?`)) return;
    alterar(prev => prev.filter((_, k) => k !== ci));
  };

  const guardarFoco = (alvo) => (e) => { focoRef.current = { ...alvo, inicio: e.target.selectionStart, fim: e.target.selectionEnd }; };

  // Clique num dado da sidebar: entra no texto focado, na posição do cursor
  const inserirDado = (marcador) => {
    const f = focoRef.current;
    if (!f || !clausulas[f.ci] || ehServicos(clausulas[f.ci])) {
      alert('Clique primeiro no texto (título ou subcláusula) onde o dado deve entrar, ou arraste o dado até lá.');
      return;
    }
    const atual = f.titulo ? clausulas[f.ci].titulo : (clausulas[f.ci].subs[f.si]?.texto ?? '');
    const inicio = Math.min(f.inicio ?? atual.length, atual.length);
    const fim = Math.min(f.fim ?? inicio, atual.length);
    const novo = atual.slice(0, inicio) + marcador + atual.slice(fim);
    if (f.titulo) alterarTitulo(f.ci, novo); else alterarSub(f.ci, f.si, novo);
    focoRef.current = { ...f, inicio: inicio + marcador.length, fim: inicio + marcador.length };
  };

  // Visualizar: gera o PDF com as cláusulas da tela (mesmo sem salvar); os dados saem como __________
  const visualizar = async () => {
    try {
      setGerandoPrevia(true);
      const token = getAuthToken();
      const resposta = await fetch(`${BASE_URL}/ContratoModelo/visualizar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ clausulas: paraServidor(editando ? clausulas : padrao) })
      });
      if (!resposta.ok) throw new Error(`Erro ${resposta.status}`);
      const blob = await resposta.blob();
      if (previa?.url) URL.revokeObjectURL(previa.url);
      setPrevia({ url: URL.createObjectURL(blob) });
    } catch (e) {
      alert('Não foi possível gerar a visualização do contrato: ' + (e.message || e));
    } finally {
      setGerandoPrevia(false);
    }
  };
  const fecharPrevia = () => { if (previa?.url) URL.revokeObjectURL(previa.url); setPrevia(null); };

  // ---- Arrastar e soltar ----
  const iniciarArrasto = (e, item) => {
    e.stopPropagation();
    arrastoRef.current = item;
    setArrastando(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', 'contrato');
  };
  const terminarArrasto = () => { arrastoRef.current = null; setArrastando(null); setAlvo(null); };

  const ladoDo = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY < r.top + r.height / 2 ? 'top' : 'bottom';
  };

  const sobreClausula = (e, ci) => {
    const a = arrastoRef.current;
    if (!a || a.tipo !== 'clausula') return;
    e.preventDefault();
    e.stopPropagation();
    setAlvo({ tipo: 'clausula', ci, lado: ladoDo(e) });
  };
  const sobreSub = (e, ci, si) => {
    const a = arrastoRef.current;
    if (!a || a.tipo !== 'sub') return;
    e.preventDefault();
    e.stopPropagation();
    setAlvo({ tipo: 'sub', ci, si, lado: ladoDo(e) });
  };
  // Subcláusula solta numa cláusula sem subcláusulas (ou no espaço dela): vai para o fim dessa cláusula
  const sobreAreaSubs = (e, ci, total) => {
    const a = arrastoRef.current;
    if (!a || a.tipo !== 'sub') return;
    e.preventDefault();
    setAlvo({ tipo: 'sub', ci, si: Math.max(0, total - 1), lado: 'bottom', vazio: total === 0 });
  };

  const soltar = (e) => {
    const a = arrastoRef.current;
    if (!a) return; // dado da sidebar solto num texto: o navegador insere no ponto do cursor
    e.preventDefault();
    e.stopPropagation();
    const t = alvo;
    terminarArrasto();
    if (!a || !t || a.tipo !== t.tipo) return;

    if (a.tipo === 'clausula') {
      alterar(prev => {
        const lista = [...prev];
        const [item] = lista.splice(a.ci, 1);
        let destino = t.lado === 'bottom' ? t.ci + 1 : t.ci;
        if (a.ci < destino) destino -= 1;
        lista.splice(destino, 0, item);
        return lista;
      });
      return;
    }

    // Subcláusula: dentro da mesma cláusula ou para outra
    alterar(prev => {
      const lista = prev.map(c => ({ ...c, subs: [...c.subs] }));
      const [item] = lista[a.ci].subs.splice(a.si, 1);
      let destino = t.vazio ? 0 : (t.lado === 'bottom' ? t.si + 1 : t.si);
      if (a.ci === t.ci && a.si < destino) destino -= 1;
      lista[t.ci].subs.splice(destino, 0, item);
      return lista;
    });
  };

  const classeBloco = (tipo, ci, si) => {
    const ehAlvo = alvo && alvo.tipo === tipo && alvo.ci === ci && (tipo === 'clausula' || alvo.si === si) && !alvo.vazio;
    const ehArrastado = arrastando && arrastando.tipo === tipo && arrastando.ci === ci && (tipo === 'clausula' || arrastando.si === si);
    return 'pc-bloco' + (ehAlvo ? (alvo.lado === 'top' ? ' drag-over-top' : ' drag-over-bottom') : '') + (ehArrastado ? ' arrastando' : '');
  };

  const editando = modo === 'personalizado';
  const lista = editando ? clausulas : padrao;

  const opcaoModo = (valor, rotulo, descricao) => (
    <label style={{ flex: '1 1 240px', display: 'flex', gap: 10, alignItems: 'flex-start', padding: 14, borderRadius: 10, cursor: 'pointer', background: '#fff', border: `2px solid ${modo === valor ? COR : '#e0e0e0'}` }}>
      <input type="checkbox" checked={modo === valor} onChange={() => { if (modo !== valor) setConfirmacao(valor); }} style={{ marginTop: 3, width: 18, height: 18, accentColor: COR }} />
      <span>
        <strong style={{ display: 'block', color: modo === valor ? COR : '#333' }}>{rotulo}</strong>
        <span style={{ fontSize: 13, color: '#666' }}>{descricao}</span>
      </span>
    </label>
  );

  return (
    <div className={'pc-pagina' + (editando ? ' com-sidebar' : '')}>
      {editando && (
        <aside className="pc-sidebar">
          <div className="pc-sidebar-header">Dados</div>
          <p className="pc-sidebar-ajuda">Arraste um dado para dentro do texto (ou clique no texto e depois no dado). No contrato, ele é trocado pelo dado de cada cliente e orçamento.</p>
          {[
            ['Cliente', m => /^\{CLIENTE_/.test(m)],
            ['Orçamento e contrato', m => !/^\{(CLIENTE_|EMPRESA_|LISTA_SERVICOS)/.test(m)],
            ['Sua empresa', m => /^\{EMPRESA_/.test(m)]
          ].map(([grupo, filtro]) => (
            <div key={grupo}>
              <div className="pc-sidebar-grupo">{grupo}</div>
              {marcadores.filter(m => filtro(campo(m, 'marcador'))).map(m => (
                <div
                  key={campo(m, 'marcador')}
                  className="pc-sidebar-campo"
                  draggable
                  title={campo(m, 'marcador')}
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', campo(m, 'marcador')); e.dataTransfer.effectAllowed = 'copy'; }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => inserirDado(campo(m, 'marcador'))}
                >
                  ⊞ {campo(m, 'descricao')}
                  <span className="pc-sidebar-dica">arrastar</span>
                </div>
              ))}
            </div>
          ))}
        </aside>
      )}
      <button type="button" className="pc-fechar" onClick={fechar} style={editando ? { left: 276 } : undefined}>✕ Fechar</button>

      <div className="pc-conteudo">
        <h2 style={{ textAlign: 'center', color: COR, margin: '8px 0 6px' }}>⚙️ Personalizar Contrato</h2>
        <p style={{ textAlign: 'center', color: '#666', margin: '0 0 16px', fontSize: 14 }}>
          Escolha se o PDF do contrato usa as cláusulas padrão do sistema ou as suas cláusulas personalizadas.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <button type="button" disabled={salvando || carregando} onClick={() => setConfirmacao('restaurar')}
            style={{ padding: '12px 28px', background: COR, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
            ↺ Restaurar padrão
          </button>
        </div>

        {carregando && <div style={{ textAlign: 'center', padding: 30, color: '#666' }}>Carregando...</div>}

        {!carregando && (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {opcaoModo('padrao', 'Padrão', 'O contrato usa as cláusulas padrão do sistema.')}
              {opcaoModo('personalizado', 'Personalizado', 'O contrato usa as suas cláusulas: edite, adicione, remova e arraste para mudar a ordem.')}
            </div>

            {erro && <div style={{ padding: 12, color: '#b00020', background: '#fdecea', borderRadius: 8, marginBottom: 14, fontWeight: 600 }}>{erro}</div>}

            {!editando && (
              <div style={{ fontSize: 13, color: '#777', textAlign: 'center', marginBottom: 12 }}>
                Este é o contrato padrão (só leitura). Marque <strong>Personalizado</strong> para editar.
              </div>
            )}

            <div className="pc-folha" onDragOver={(e) => { if (arrastoRef.current) e.preventDefault(); }} onDrop={soltar}>
              {/* Parte fixa: sempre a primeira, igual ao PDF; não edita, não arrasta, não remove */}
              <div className="pc-fixo">
                <span className="pc-fixo-selo">🔒 Fixo</span>
                <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, marginBottom: 10 }}>CONTRATO DE PRESTACAO DE SERVICOS</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>DAS PARTES</div>
                <p style={{ margin: '0 0 6px' }}><strong>CONTRATADA:</strong> __________, pessoa jurídica/física de direito privado, inscrita no CNPJ/CPF n. <strong>__________</strong>, com sede em __________, __________/____, doravante denominada CONTRATADA.</p>
                <p style={{ margin: '0 0 6px' }}><strong>CONTRATANTE:</strong> __________, pessoa jurídica/física de direito privado, inscrita no CNPJ/CPF n. <strong>__________</strong>, com sede/residente em __________, __________/____, doravante denominado(a) CONTRATANTE.</p>
                <p style={{ margin: 0, fontStyle: 'italic' }}>Decidem as partes, na melhor forma de direito, celebrar o presente CONTRATO DE PRESTACAO DE SERVICOS, que reger-se-a mediante as clausulas e condicoes adiante estipuladas.</p>
              </div>

              {lista.map((c, ci) => (
                <div
                  key={c.id}
                  className={editando ? classeBloco('clausula', ci) : 'pc-bloco'}
                  onDragOver={editando ? (e) => sobreClausula(e, ci) : undefined}
                  onDrop={editando ? soltar : undefined}
                  style={{ marginTop: 10 }}
                >
                  {editando && <Alca onDragStart={(e) => iniciarArrasto(e, { tipo: 'clausula', ci })} onDragEnd={terminarArrasto} />}
                  {editando && <button type="button" className="pc-bloco-remover" title="Remover cláusula" onClick={() => removerClausula(ci)}>×</button>}

                  <div className="pc-titulo">
                    <span style={{ whiteSpace: 'nowrap' }}>CLAUSULA {ordinal(ci + 1)} -</span>
                    {editando && !ehServicos(c)
                      ? <input value={c.titulo} onChange={(e) => alterarTitulo(ci, e.target.value)} onFocus={guardarFoco({ ci, titulo: true })} onSelect={guardarFoco({ ci, titulo: true })} onKeyUp={guardarFoco({ ci, titulo: true })} onClick={guardarFoco({ ci, titulo: true })} placeholder="TÍTULO DA CLÁUSULA" />
                      : <span>{c.titulo}</span>}
                    {ehServicos(c) && <span className="pc-fixo-selo" style={{ float: 'none', marginLeft: 'auto' }}>🔒 Obrigatória · só muda de posição</span>}
                  </div>
                  {avisoObrigatoria === c.id && (
                    <div style={{ margin: '6px 0', padding: '8px 10px', background: '#fdecea', color: '#b00020', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                      Essa cláusula é obrigatória: ela lista os serviços do contrato e os valores. Ela não pode ser removida nem editada, só arrastada para outra posição.
                    </div>
                  )}

                  <div className="pc-subs" onDragOver={editando && !ehServicos(c) ? (e) => sobreAreaSubs(e, ci, c.subs.length) : undefined}>
                    {c.subs.map((s, si) => (
                      <div
                        key={s.id}
                        className={editando && !ehServicos(c) ? classeBloco('sub', ci, si) : 'pc-bloco'}
                        onDragOver={editando && !ehServicos(c) ? (e) => sobreSub(e, ci, si) : undefined}
                        onDrop={editando && !ehServicos(c) ? soltar : undefined}
                      >
                        {editando && !ehServicos(c) && <Alca onDragStart={(e) => iniciarArrasto(e, { tipo: 'sub', ci, si })} onDragEnd={terminarArrasto} />}
                        {editando && !ehServicos(c) && <button type="button" className="pc-bloco-remover" title="Remover subcláusula" onClick={() => removerSub(ci, si)}>×</button>}
                        <div className="pc-sub">
                          <span className="pc-sub-numero">{ci + 1}.{si + 1}</span>
                          {editando && !ehServicos(c)
                            ? <TextoAuto value={s.texto} onChange={(v) => alterarSub(ci, si, v)} onFocus={guardarFoco({ ci, si })} placeholder="Texto da subcláusula" />
                            : <span style={{ padding: '4px 0', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{s.texto.replace('{LISTA_SERVICOS}', '__________: R$ __________').replace(/\{[A-Z_]+\}/g, '__________')}</span>}
                        </div>
                      </div>
                    ))}
                    {editando && !ehServicos(c) && (
                      <button type="button" className="pc-add" onClick={() => adicionarSub(ci)} style={{ marginLeft: 8 }}>
                        + Subcláusula ({ci + 1}.{c.subs.length + 1})
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {editando && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <button type="button" className="pc-add" onClick={adicionarClausula} style={{ padding: '10px 20px', fontSize: 14 }}>
                    + Adicionar cláusula (CLAUSULA {ordinal(clausulas.length + 1)})
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {!carregando && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <button type="button" onClick={visualizar} disabled={gerandoPrevia}
              style={{ padding: '12px 30px', background: COR, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
              {gerandoPrevia ? 'Gerando PDF...' : '👁️ Visualizar PDF'}
            </button>
          </div>
        )}
      </div>

      {previa && (
        <div onClick={fecharPrevia} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 900, height: '90vh', background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <div>
                <strong style={{ color: COR }}>Visualização do contrato</strong>
                <div style={{ fontSize: 12, color: '#777' }}>Os dados de cada contrato aparecem como __________ e são preenchidos automaticamente.{alterado ? ' Esta prévia ainda não foi salva.' : ''}</div>
              </div>
              <button type="button" onClick={fecharPrevia} aria-label="Fechar" style={{ flexShrink: 0, background: COR, color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
            </div>
            <iframe title="Visualização do contrato" src={previa.url} style={{ flex: 1, border: 'none', width: '100%' }} />
          </div>
        </div>
      )}

      {/* Salvar fixo, no lugar do botão do chat */}
      {editando && !carregando && (
        <>
          {alterado && <div className="pc-salvar-aviso">Alterações não salvas</div>}
          <button type="button" className="pc-salvar" disabled={salvando} onClick={() => salvar('personalizado')}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      )}

      {confirmacao && (
        <div onClick={() => setConfirmacao(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 12, padding: 22, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
            <h3 style={{ margin: '0 0 10px', color: COR }}>Tem certeza?</h3>
            <p style={{ margin: '0 0 18px', color: '#444', fontSize: 14, lineHeight: 1.5 }}>
              {confirmacao === 'personalizado' && <>Aqui você vai conseguir <strong>personalizar o seu contrato</strong>: editar as cláusulas, adicionar novas cláusulas e subcláusulas, remover e arrastar para mudar a ordem. O contrato padrão completo será carregado para você começar. As mudanças só valem nos contratos depois que você clicar em <strong>Salvar</strong>.</>}
              {confirmacao === 'padrao' && <>Os contratos voltarão a usar as <strong>cláusulas padrão</strong> do sistema. Suas cláusulas personalizadas ficam guardadas para você voltar a usar depois.</>}
              {confirmacao === 'restaurar' && <>O contrato volta ao <strong>padrão do sistema</strong> e <strong>todas as suas personalizações serão apagadas</strong>. Isso não pode ser desfeito.</>}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" onClick={() => setConfirmacao(null)} style={{ padding: '10px 18px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, background: '#6c757d', color: '#fff' }}>Não</button>
              <button type="button" onClick={confirmar} style={{ padding: '10px 22px', background: COR, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}>Sim</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
